/**
 * Bot tuning: every judgement number the bot modules used to carry inline,
 * as one typed object (`BotTuning`, types.ts) with a default. Skill and
 * personality stay what they are (execution quality, strategic preference);
 * tuning is the third axis — how those turn into scores, thresholds and
 * search budgets.
 *
 * `BotConfig.tuning` overrides any part of it per bot (difficulty presets,
 * campaign "configured" bots, experiments); `resolveTuning` merges a partial
 * onto `DEFAULT_BOT_TUNING`. The defaults reproduce the pre-tuning behaviour
 * exactly — tests/botGolden.test.ts is the proof.
 *
 * Not here on purpose: the flight-outcome score bands in scoring.ts (mirrored
 * bit-for-bit in the WASM kernel), the fire-search grid geometry (gravity
 * seeds, focused/refine ranges) and the jump search grids — search mechanics
 * rather than judgement.
 */

import type { BotTuning, BotTuningOverrides, FireSearchTier } from '../../types';

export type { BotTuning, BotTuningOverrides, FireSearchTier, WeaponPreference } from '../../types';
import type { BotConfig } from '../../types';
import { PICTURE_SPREAD, PICTURE_SPREAD_PER_TURN, PICTURE_MIN_CONFIDENCE } from '../../constants';

export const DEFAULT_BOT_TUNING: BotTuning = {
  action: {
    fireBase: 0.4, fireAggression: 0.3, fireAmmoBonus: 0.2,
    moveBase: 0.1, moveMobility: 0.3, moveEnemySamePlanetBonus: 0.1, moveLowHealthBelow: 40, moveLowHealthBonus: 0.2,
    jumpBase: 0.05, jumpMobility: 0.3, jumpRisk: 0.1, jumpGraphMinSkill: 0.6, jumpGraphBonus: 0.15, jumpFuelBonus: 0.1,
    buildBase: 0.15, buildAggressionPenalty: 0.2, buildRiskPenalty: 0.1, buildFirstBonus: 0.3, buildMinCbp: 3,
    shopBase: 0.2, shopUrgency: 0.3, shopMinCbp: 5,
    skip: 0.05,
    healBase: 0.1, healMissingHealth: 0.8, healCaution: 0.2, healCriticalBelow: 30, healCriticalBonus: 0.4,
    probeWhenBlindBonus: 0.25,
    noise: 0.4,
  },
  postFire: {
    moveBase: 0.35, moveMobility: 0.4, moveSpotted: 0.3, moveMissingHealth: 0.2,
    jumpBase: 0.05, jumpMobility: 0.25, jumpSpotted: 0.2, jumpRisk: 0.15, jumpPlanetDamage: 0.15,
    skipBase: 0.10, skipStayPut: 0.2, skipSpotted: 0.3,
    objectiveMinSkill: 0.3,
    samePlanetObjectiveMoveBonus: 0.7,
    carrierJumpBonus: 1.2, carrierMoveBonus: 0.6, objectiveMoveBonus: 0.7,
    alwaysMoveMinSkill: 0.8, alwaysMoveMaxAggression: 0.4, alwaysMoveBonus: 0.4, alwaysMoveSkipPenalty: 0.3,
    coverHoldBonus: 0,
    noise: 0.3,
  },
  targeting: {
    distanceFalloff: 0.05,
    healthMinSkill: 0.3, threatMinSkill: 0.6,
    threatTurnScale: 20, threatSamePlanetBonus: 0.3,
    reachAdjacentDistance: 40, reachAdjacent: 0.7, reachFar: 0.3,
    mediumDistanceWeight: 0.5, mediumHealthWeight: 0.5,
    aggressiveHealthWeight: 0.6, aggressiveThreatWeight: 0.4,
    cautiousDistanceWeight: 0.5, cautiousReachWeight: 0.5,
    noise: 0.5, ghostPenalty: 0.5, prioritizeCommandBonus: 0.5,
  },
  weapon: {
    chaoticMinRisk: 0.8, chaoticMinMobility: 0.6,
    contextMinSkill: 0.4,
    overkillTargetHealth: 15, overkillDamage: 40, overkillPenalty: 0.3,
    hitscanSamePlanetRange: 15, hitscanSamePlanetBonus: 3.0, hitscanCloseRange: 8, hitscanCloseBonus: 2.0, hitscanFarPenalty: 0.1,
    distanceNorm: 40, blastNorm: 5,
    scarceAmmo: 2, scarceAmmoPenalty: 0.5,
    collateralBlastRadius: 3, collateralPenalty: 0.3,
    fieldSamePlanetBonus: 3,
    minWeight: 0.01,
    preference: {
      standard:    { base: 3 },
      heavy:       { base: 2, aggression: 3, riskSkillGate: 0.3, riskSkillBonus: 2 },
      cluster:     { base: 1, aggression: 2 },
      nuke:        { base: 2, aggression: 3, nukeRiskGate: 0.5, nukeOff: 0.1 },
      probe:       { base: 1, invAggression: 2 },
      punch:       { base: 1.5, invRisk: 2 },
      antigrav:    { base: 1, mobility: 2 },
      gravBomb:    { base: 1, aggression: 1.5, risk: 1.5 },
      tether:      { base: 1, invMobility: 2 },
      terraformer: { base: 0.5, invAggression: 2 },
      emp:         { base: 1, aggression: 1.5 },
      drill:       { base: 1, aggression: 1.5 },
      fire:        { base: 1.5, aggression: 2 },
      mortar:      { base: 2, aggression: 1 },
    },
  },
  scouting: {
    unprobedRevealFraction: 0.3, maxAttemptsWithGhosts: 4, maxAttemptsBlind: 5,
    surveyRadius: 8.0, maxSteps: 7200, selfHitGraceSteps: 600,
    azimuthSteps: 20,
    elevations: [0.3, 0.45, 0.6, 0.7, 0.8, 0.9, 1.0, 1.1, 1.2, 1.35],
    powers: [20, 35, 50, 70, 90],
    unprobedWeight: 1000, flightTiebreak: 0.1,
    scatterAzimuth: 0.15, scatterElevation: 0.1, scatterPower: 8,
    useAwareness: false, freshTurns: 1, staleTurns: 3, staleWeight: 500, probeValueThreshold: 0.5,
  },
  fire: {
    railgunRange: 15,
    hitscanAimNoise: 0.15,
    reengageDistance: 2.0, reengageSameSpotDot: 0.99995,
    reengageAzimuthScale: 0.3, reengageElevationScale: 0.2, reengagePowerScale: 10,
    ghostNoise: 0.15, blindNoise: 0.3, skillDamp: 0.8,
    finalAzimuthScale: 0.5, finalElevationScale: 0.3, finalPowerScale: 15,
    searchFlightSeconds: 25, refliedFinalists: 8, escapeTest: true,
    roundScoredCandidates: 0,
    searchTiers: [
      { minSkill: 0.9, coarseAzSteps: 24, coarseElevSteps: 8, coarsePowSteps: 6, focusedTopN: 8, focusedGridSize: 7, refinePasses: 6, randomSeeds: 20 },
      { minSkill: 0.7, coarseAzSteps: 18, coarseElevSteps: 6, coarsePowSteps: 5, focusedTopN: 6, focusedGridSize: 5, refinePasses: 6, randomSeeds: 12 },
      { minSkill: 0.4, coarseAzSteps: 12, coarseElevSteps: 5, coarsePowSteps: 4, focusedTopN: 4, focusedGridSize: 5, refinePasses: 4, randomSeeds: 8 },
      { minSkill: 0, coarseAzSteps: 8, coarseElevSteps: 4, coarsePowSteps: 3, focusedTopN: 2, focusedGridSize: 5, refinePasses: 0, randomSeeds: 4 },
    ],
  },
  movement: {
    coverWeight: 0,
    candidateCount: 8, candidateOffset: 0.6,
    closeInAbove: 0.5,
    chaoticMinRisk: 0.7, aggressiveAbove: 0.6, defensiveBelow: 0.4,
    losNoise: 2, repositionMin: 0.1, repositionMax: 0.3,
  },
  jumping: {
    escapeHealthBelow: 30, escapePlanetDamageAbove: 0.3, aggressiveAbove: 0.6, defensiveBelow: 0.4,
    // Landing judgement on by default (decision 90, Oct 9): survive every landing, take the softest hit found.
    // `JUMP_PROFILES.reckless` is the pre-37.13 behaviour (first hit wins, whatever it costs).
    maxLandingHealthFraction: 1, maxLandingDamage: Infinity, preferSofterLanding: true, landingCandidates: 8,
    // Reach: today's search — 3-hop routes, 10 s flights, slingshot seeds, full sweep, refinement
    maxHops: 3, maxFlightSeconds: 10, gravityAssist: true, fullSweep: true, refineNearMisses: true,
  },
  economy: {
    aggressiveAbove: 0.6, defensiveBelow: 0.4, chaoticMinRisk: 0.7,
    lowTotalSpecial: 5, lowPriorityAmmo: 3,
    urgencyEmpty: 1.0, urgencyLow: 0.6, urgencyLowAt: 2, urgencyMid: 0.2, urgencyMidAt: 5,
    shopStockedAt: 3, shopFuelDeliveryMultiplier: 3, mobileFuelAbove: 0.5,
    loadoutFuelTransit: 3, loadoutFuelMobile: 2, loadoutFuelMobileAbove: 0.7, loadoutFuelDefault: 1,
  },
  intel: {
    teammateSeedDistance: 5.0,
  },
  flow: {
    // Off by default: the bot plans a round exactly as it plays a unit-by-unit turn
    teammateQueuedTargetFactor: 1, uncommittedTargetFactor: 1, leadPlannedWalks: false, uncommittedBlastWeight: 0,
    planAgainstQueue: false,
    expectedDamageTargeting: false, ghostDamageConfidence: 0.5, uncommittedDamageConfidence: 0.6, deadOnPaperFactor: 0.15, displacedTargetConfidence: 0.3, teamMarginalValue: false, researchBelowFraction: 0, firstPlannerDefers: 0, teamPlan: false, teamPlanAlternatives: 1,
  },
  mounting: {
    takeSeats: false,
  },
  stealth: {
    sneak: false, goLoudRoundsLeft: 0, landingHealthFraction: 1, goLoudStalledTurns: 0,
  },
  asks: { dangerCloseBlasts: 2 },
  picture: { spread: { ...PICTURE_SPREAD }, spreadPerTurn: PICTURE_SPREAD_PER_TURN, minConfidence: PICTURE_MIN_CONFIDENCE },
};

/**
 * Phase 57: the infiltrator's stance — the sector's `stance: sneak` resolves to this.
 * No landing reserve (57.10d): measured at 1 / 0.6 / 0.5, the reserve only skipped jumps — the infiltrator kit's
 * padding already makes the round trip, and Salvage lost two wins to it. The dial stays (`--landing-reserve`).
 */
/** Phase 58 step 3: scouting from the team's awareness (scan vs engage) — measured before any level carries it. */
export const SCOUT_PROFILES: Record<'counts' | 'awareness', Partial<BotTuning['scouting']>> = {
  counts: {},
  awareness: { useAwareness: true, freshTurns: 0, staleTurns: 1, probeValueThreshold: 0.5 },
};

export const STEALTH_PROFILES: Record<'assault' | 'sneak' | 'sneakQuiet' | 'sneakStalled', Partial<BotTuning['stealth']>> = {
  assault: {},
  /** Sneak until the clock says otherwise: with a turn limit, go loud when fewer than 6 rounds remain. */
  sneak: { sneak: true, goLoudRoundsLeft: 6 },
  /** Sneak to the end, whatever the clock (the third fill's stance; the A/B control). */
  sneakQuiet: { sneak: true, goLoudRoundsLeft: 0 },
  /** 57.7c: the objective-aware clock — sneak until quiet progress has stalled for 4 turns while not leading, then go loud. */
  sneakStalled: { sneak: true, goLoudRoundsLeft: 0, goLoudStalledTurns: 4 },
};

/** Phase 60: how the fire search picks among its finalists — by miss distance (today) or by the round each would start. */
export const FIRE_PROFILES: Record<'distance' | 'roundScored' | 'roundScoredWide', Partial<BotTuning['fire']>> = {
  distance: {},
  roundScored: { roundScoredCandidates: 6 },
  roundScoredWide: { roundScoredCandidates: 12 },
};

/**
 * Named round-flow profiles (Phase 47). `unaware` is the default; `aware` spreads
 * the team's fire across targets, prefers enemies that can no longer move this
 * round, aims at a visible enemy's planned destination, and reaches for wider
 * blasts against enemies that can still walk.
 */
export const FLOW_PROFILES: Record<'unaware' | 'aware' | 'queueAware' | 'targetAware' | 'teamPlan', Partial<BotTuning['flow']>> = {
  unaware: {},
  aware: { teammateQueuedTargetFactor: 0.3, uncommittedTargetFactor: 0.5, leadPlannedWalks: true, uncommittedBlastWeight: 0.6 },
  /** Phase 49: the planner dry-runs the team's queued shots and aims at the world they leave (no targeting-weight changes). */
  queueAware: { planAgainstQueue: true },
  /** Expected-damage targeting: do not shoot what the queue already kills (owner, Oct 2026). */
  // researchBelowFraction and firstPlannerDefers measured Oct 9 (sweeps/2026-10-09-first-planner.md): no gain — left at their defaults (off)
  targetAware: { expectedDamageTargeting: true, teamMarginalValue: true },
  /** Team-level planning: the first bot to plan chooses the team's shots jointly (measured before any level carries it). */
  teamPlan: { teamPlan: true },
};

/**
 * Named jump profiles (37.13): how much landing damage a bot will accept.
 * `careful` is the default (decision 90): it survives every landing and takes the
 * softest hit it finds; `reckless` takes any landing that reaches the target (the
 * pre-37.13 behaviour); `cautious` also caps the cost at a third of its health.
 */
export const JUMP_PROFILES: Record<'reckless' | 'careful' | 'cautious', Partial<BotTuning['jumping']>> = {
  reckless: { maxLandingHealthFraction: Infinity, preferSofterLanding: false },
  careful: { maxLandingHealthFraction: 1, preferSofterLanding: true },
  cautious: { maxLandingHealthFraction: 1 / 3, preferSofterLanding: true },
};

/**
 * Jump reach profiles (37.12): how a bot looks for jumps. `full` is the default
 * (today's search: any route the preview can find). `person` jumps the way a
 * player would — direct or one relay, nothing that flies longer than 6 s, no
 * slingshots. `grounded` takes only the obvious direct hop. `ridiculous` is the
 * specialist / story-bot setting: longer routes and flights than the default.
 */
export const JUMP_REACH_PROFILES: Record<'grounded' | 'person' | 'full' | 'ridiculous', Partial<BotTuning['jumping']>> = {
  grounded: { maxHops: 1, maxFlightSeconds: 4, gravityAssist: false, fullSweep: false, refineNearMisses: false },
  person: { maxHops: 2, maxFlightSeconds: 6, gravityAssist: false, fullSweep: true, refineNearMisses: true },
  full: {},
  ridiculous: { maxHops: 4, maxFlightSeconds: 20, gravityAssist: true, fullSweep: true, refineNearMisses: true },
};

/** Per-section overrides merged onto the defaults (no skill profile — the raw mechanism). */
export function resolveTuning(overrides?: BotTuningOverrides): BotTuning {
  if (!overrides) return DEFAULT_BOT_TUNING;
  const out: Record<string, unknown> = { ...DEFAULT_BOT_TUNING };
  for (const key of Object.keys(overrides) as Array<keyof BotTuning>) {
    const o = overrides[key];
    if (o === undefined) continue;
    out[key] = { ...(DEFAULT_BOT_TUNING[key] as object), ...(o as object) };
  }
  return out as unknown as BotTuning;
}

/** The fire-search budget row for a skill level. */
export function fireSearchTier(tuning: BotTuning, skill: number): FireSearchTier {
  for (const tier of tuning.fire.searchTiers) if (skill >= tier.minSkill) return tier;
  return tuning.fire.searchTiers[tuning.fire.searchTiers.length - 1];
}

/**
 * Skill profiles (Phase 53): what a skill level *decides*, not just how straight it aims.
 * Until now BOT_SKILL_LEVELS only scaled aim noise and the fire-search budget; the
 * judgement profiles measured in Phases 43–49 were opt-in. Now each level carries
 * the profile the measurements favoured, so "expert" means better decisions:
 *   easy    — jumps only the obvious direct hop (grounded reach), reckless landings
 *   medium  — jumps like a person (direct or one relay, ≤ 6 s), reckless landings
 *   hard    — like a person, and survives its landings (careful)
 *   expert  — like a person, careful, and plans against the team's queue in round flows
 * Per-bot `tuning` overrides still win over the profile; `skillProfile: false` ignores it.
 */
export type SkillLevel = 'easy' | 'medium' | 'hard' | 'expert';
export const SKILL_PROFILES: Record<SkillLevel, BotTuningOverrides> = {
  easy: { jumping: { ...JUMP_REACH_PROFILES.grounded } },
  medium: { jumping: { ...JUMP_REACH_PROFILES.person } },
  hard: { jumping: { ...JUMP_REACH_PROFILES.person, ...JUMP_PROFILES.careful } },
  // Phase 60.1: expert re-judges its best shots by the round they start (+5 points, +17 % kills in every mirror cell)
  expert: { jumping: { ...JUMP_REACH_PROFILES.person, ...JUMP_PROFILES.careful }, flow: { ...FLOW_PROFILES.queueAware, ...FLOW_PROFILES.targetAware }, fire: { ...FIRE_PROFILES.roundScored } },
};
/** The skill level a 0–1 skill belongs to (BOT_SKILL_LEVELS: easy 0.2, medium 0.5, hard 0.8, expert 1.0). */
export function skillLevelFor(skill: number): SkillLevel {
  return skill >= 0.95 ? 'expert' : skill >= 0.7 ? 'hard' : skill >= 0.4 ? 'medium' : 'easy';
}

/** The tuning a bot actually uses: defaults ← its skill level's profile ← its own overrides. */
export function tuningFor(config: Pick<BotConfig, 'skill' | 'tuning' | 'skillProfile'> & Partial<BotConfig>): BotTuning {
  if (config.skillProfile === false) return resolveTuning(config.tuning);
  const profile = SKILL_PROFILES[skillLevelFor(config.skill)];
  if (!config.tuning) return resolveTuning(profile);
  const merged: Record<string, unknown> = { ...profile };
  for (const key of Object.keys(config.tuning) as Array<keyof BotTuning>) {
    const o = config.tuning[key];
    if (o === undefined) continue;
    merged[key] = { ...((profile[key] as object) ?? {}), ...(o as object) };
  }
  return resolveTuning(merged as BotTuningOverrides);
}
